/**
 * Jitsi Configuration
 * 
 * Supports both public Jitsi (meet.jit.si) and JaaS (8x8.vc)
 * Configure via environment variables:
 * - VITE_JITSI_SERVER_URL: The Jitsi server URL (default: https://meet.jit.si)
 * - VITE_JITSI_APP_ID: JaaS App ID (only needed for 8x8.vc)
 */

import { meetingsApi } from './api';

// Jitsi configuration from environment
export const JITSI_CONFIG = {
  serverUrl: import.meta.env.VITE_JITSI_SERVER_URL || 'https://meet.jit.si',
  appId: import.meta.env.VITE_JITSI_APP_ID || '',
  
  // Derived values
  get isJaaS(): boolean {
    return this.serverUrl.includes('8x8.vc') && !!this.appId;
  },
  
  get domain(): string {
    // Extract domain from URL (e.g., "meet.jit.si" from "https://meet.jit.si")
    try {
      return new URL(this.serverUrl).host;
    } catch {
      return 'meet.jit.si';
    }
  },
};

/**
 * Build the Jitsi meeting join URL (without JWT - fallback)
 * 
 * For JaaS: https://8x8.vc/{appId}/{roomId}
 * For public Jitsi: https://meet.jit.si/{roomId}
 */
export function buildJitsiJoinUrl(roomId: string, jwt?: string): string {
  let baseUrl: string;
  
  if (JITSI_CONFIG.isJaaS) {
    // JaaS format: https://8x8.vc/{appId}/{roomId}
    baseUrl = `${JITSI_CONFIG.serverUrl}/${JITSI_CONFIG.appId}/${roomId}`;
  } else {
    // Public Jitsi format: https://meet.jit.si/{roomId}
    baseUrl = `${JITSI_CONFIG.serverUrl}/${roomId}`;
  }
  
  // Append JWT if provided (for authenticated JaaS sessions)
  if (jwt) {
    return `${baseUrl}?jwt=${jwt}`;
  }
  
  return baseUrl;
}

/**
 * Join a meeting with secure JWT authentication
 * 
 * This function:
 * 1. Requests a JWT token from the backend
 * 2. Opens the meeting with the JWT (if JaaS configured)
 * 3. Falls back to public Jitsi if JaaS not configured
 * 
 * @param meetingId - The meeting ID
 * @param options - Optional join options
 * @returns Promise with the meeting URL that was opened
 */
export async function joinMeetingSecure(
  meetingId: string,
  options: { openInNewTab?: boolean } = {}
): Promise<{ url: string; isModerator: boolean }> {
  const { openInNewTab = true } = options;

  try {
    console.log('Requesting JWT token for meeting:', meetingId);
    
    // Get JWT token from backend
    const response = await meetingsApi.getJoinToken(meetingId);
    
    console.log('JWT response:', response.data);
    
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get meeting token');
    }

    const { meetingUrl, isModerator, usePublicJitsi, token } = response.data.data;

    console.log('Meeting URL:', meetingUrl);
    console.log('Is Moderator:', isModerator);
    console.log('Using Public Jitsi:', usePublicJitsi);
    console.log('Has JWT Token:', !!token);

    // Open the meeting
    if (openInNewTab) {
      window.open(meetingUrl, '_blank');
    }

    return { url: meetingUrl, isModerator };
  } catch (error) {
    console.error('Failed to join meeting securely:', error);
    throw error;
  }
}

/**
 * Jitsi iframe API options for embedded meetings
 */
export interface JitsiMeetOptions {
  roomName: string;
  jwt?: string;
  userInfo?: {
    displayName: string;
    email: string;
    avatarUrl?: string;
  };
  configOverwrite?: Record<string, unknown>;
  interfaceConfigOverwrite?: Record<string, unknown>;
}

/**
 * Get default Jitsi options for embedding
 */
export function getJitsiEmbedOptions(options: JitsiMeetOptions) {
  return {
    roomName: JITSI_CONFIG.isJaaS 
      ? `${JITSI_CONFIG.appId}/${options.roomName}` 
      : options.roomName,
    jwt: options.jwt,
    userInfo: options.userInfo,
    configOverwrite: {
      startWithAudioMuted: true,
      startWithVideoMuted: true,
      prejoinPageEnabled: true,
      disableDeepLinking: true,
      ...options.configOverwrite,
    },
    interfaceConfigOverwrite: {
      SHOW_JITSI_WATERMARK: false,
      SHOW_WATERMARK_FOR_GUESTS: false,
      SHOW_BRAND_WATERMARK: false,
      BRAND_WATERMARK_LINK: '',
      SHOW_POWERED_BY: false,
      TOOLBAR_BUTTONS: [
        'microphone',
        'camera',
        'closedcaptions',
        'desktop',
        'fullscreen',
        'fodeviceselection',
        'hangup',
        'chat',
        'recording',
        'livestreaming',
        'settings',
        'raisehand',
        'videoquality',
        'filmstrip',
        'participants-pane',
        'tileview',
        'select-background',
        'mute-everyone',
      ],
      ...options.interfaceConfigOverwrite,
    },
  };
}

